// Treasury — system accounts (SM-152). The platform-owned account tree
// where budget is minted (SYSTEM_DEPOSIT), allocated from fee revenue
// (PLATFORM_REVENUE → SYSTEM_*), and drained into promotions / MM
// funding. The virtual PLATFORM_REVENUE root renders alongside the
// four seeded SYSTEM_* roots; children expand beneath their parents.
// Click a row for the per-account detail surface (balances + mint/burn
// + transfer + children + movements + audit).
//
// ONE CURRENCY AT A TIME (SM-500). Since ledger migration 0025 a system
// account exists once per (account_code, currency), so this table was
// listing SYSTEM_TREASURY three times — same name, same code, three
// different balances, nothing above them saying which was which. The
// currency picker makes the screen answer one question instead of three
// interleaved ones, and it defaults to GOLD: that is what every system
// account held before the epic backfilled them, so an operator who
// never touches the picker sees the treasury they have always seen.
//
// The choice lives in the URL (`?currency=`), not in component state,
// for three reasons that all matter here: a reload or a bookmark keeps
// it, a link into the detail page inherits it, and coming BACK from a
// detail page lands on the currency the operator left.
//
// Routing note: `/treasury/accounts/$code` is a file-based child of
// this route, so we use the same conditional-outlet pattern
// treasury.tsx uses — on any deeper path, render the Outlet alone so
// the detail page owns the screen.
import { createFileRoute, Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { fetchSystemAccounts, keys } from "@/lib/queries";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/Card";
import { Button } from "@/components/ui/Button";
import { Skeleton } from "@/components/ui/Skeleton";
import { ErrorPanel } from "@/components/ui/ErrorPanel";
import { SystemAccountTree } from "@/components/treasury/SystemAccountTree";
import {
  AllocateRevenueDialog,
  findPlatformRevenue,
} from "@/components/treasury/AllocateRevenueDialog";
import { LedgerCurrencyFilter } from "@/components/currency/LedgerCurrencyFilter";
import { useCurrencyName } from "@/components/currency/CurrencyPresentationProvider";
import { campaignCurrency, isCurrencyCode, type CurrencyCode } from "@/lib/currency";
import { useMyPerms } from "@/hooks/useMyPerms";
import { Perm } from "@/lib/perms";

export const Route = createFileRoute("/_app/treasury/accounts")({
  // OPTIONAL in the URL, GOLD in the component — the same rule the
  // detail route and the API itself apply to an absent `?currency=`
  // (G3). Making it required would put a currency in every link the
  // console already has to this page just to restate the default.
  validateSearch: (search: Record<string, unknown>): { currency?: CurrencyCode } =>
    typeof search.currency === "string" && isCurrencyCode(search.currency)
      ? { currency: search.currency }
      : {},
  loader: ({ context }) =>
    context.queryClient.ensureQueryData({
      queryKey: keys.systemAccounts(),
      queryFn: fetchSystemAccounts,
    }),
  component: SystemAccountsPage,
});

function SystemAccountsPage() {
  const { location } = useRouterState();
  // /treasury/accounts is the tree; anything deeper (the $code detail)
  // renders via <Outlet />. Same guard as treasury.tsx.
  if (location.pathname !== "/treasury/accounts") return <Outlet />;
  return <SystemAccountsTreePage />;
}

function SystemAccountsTreePage() {
  const perms = useMyPerms();
  const navigate = useNavigate();
  const currency = campaignCurrency(Route.useSearch().currency);
  const currencyName = useCurrencyName(currency);
  const [allocating, setAllocating] = useState(false);

  const accounts = useQuery({
    queryKey: keys.systemAccounts(),
    queryFn: fetchSystemAccounts,
  });

  // The list endpoint returns every currency's rows in one flat payload
  // (it is operator-curated and small, so it is not paginated and takes
  // no filter). Narrowing here rather than upstream keeps switching
  // currency instant and leaves ONE cached copy of the tree — the
  // alternative is a request per currency for data we already hold.
  const all = accounts.data ?? [];
  const rows = all.filter((a) => campaignCurrency(a.currency) === currency);

  // Gate the action on the SAME resolution the dialog performs
  // (SM-521): a button that appears because *some* revenue root exists,
  // over a dialog that then looks one up by (code, currency), is two
  // predicates that can disagree — offered-then-renders-nothing.
  const hasRevenueRoot = findPlatformRevenue(all, currency) !== undefined;

  const setCurrency = (next: CurrencyCode) =>
    navigate({ to: ".", search: (prev) => ({ ...prev, currency: next }), replace: true });

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl tracking-tight">System accounts</h1>
          <p className="text-sm text-muted">
            Platform-owned balances: mint budget, allocate fee revenue, and fund
            promotions / market making from explicit, auditable accounts.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <LedgerCurrencyFilter value={currency} onChange={setCurrency} className="w-52" />
          {/* Allocating revenue moves money OUT of the revenue root, so
              it is offered only when this currency has one. */}
          {perms.has(Perm.SystemAccountAllocateRevenue) && hasRevenueRoot && (
            <Button onClick={() => setAllocating(true)}>Allocate revenue</Button>
          )}
        </div>
      </div>

      <Card>
        <CardHeader className="border-b border-border">
          <CardTitle>Accounts</CardTitle>
          {/* Counts the currency being shown, not the payload: a header
              saying 51 over a table of 17 rows reads as a broken table. */}
          <span className="text-2xs text-muted">{rows.length}</span>
        </CardHeader>
        <CardBody className="p-0">
          {accounts.isLoading && (
            <div className="space-y-2 p-5">
              {[0, 1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10" />
              ))}
            </div>
          )}
          {accounts.isError && (
            <ErrorPanel error={accounts.error} reset={() => accounts.refetch()} />
          )}
          {accounts.data && (
            <SystemAccountTree
              accounts={rows}
              // "No system accounts yet" would be a lie with a filter
              // above the table: there ARE accounts, just none holding
              // this currency, and those are different problems.
              emptyLabel={
                all.length
                  ? `No system account holds ${currencyName} yet — pick another currency above.`
                  : "No system accounts yet."
              }
            />
          )}
        </CardBody>
      </Card>

      <AllocateRevenueDialog
        open={allocating}
        accounts={all}
        currency={currency}
        onClose={() => setAllocating(false)}
      />
    </div>
  );
}
